import { useCallback, useEffect, useMemo, useState } from 'react';
import { flatMap, filter, isArray, isObject } from 'lodash';
import { FullscreenOutlined } from '@ant-design/icons';
import { Row, Col, Card, Tag, Button, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import ServiceAreaMap from '../service_area_map/ServiceAreaMap';
import EditSupplierServiceAreasForm from '../edit_supplier_service_areas_form/EditSupplierServiceAreasForm';
import { resolveServiceAreaBoundariesFromCDN } from '../../utils/geoBoundariesApi';
import { consolidateServiceAreasFromCdn } from '../../utils/serviceAreaCdnConsolidation';
import {
	getCountryFromArea,
	getStateFromArea,
	getCenterOnTargetForAreas,
	serviceAreaFromApiItem,
	USA_DEFAULT_CENTER_ON,
} from '../edit_supplier_service_areas_form/utils';
import { fetchSupplierServiceAreas } from '../../thunks/supplier_facilities_thunks';
import useMountEffect from '../../hooks/useMountEffect';

const SERVICE_AREAS_CARD_MAX_VISIBLE_TAGS = 6;
const BUCKET_NAMES = ['countries', 'states', 'counties'];

const serviceAreaItemToDisplayShape = (item: any, defaultPlaceType: string): any => ({
	name: String(item.name || ''),
	placeType: item.placeType || defaultPlaceType,
	center: item.center && item.center.length === 2 ? [item.center[0], item.center[1]] : [0, 0],
	bbox: item.bbox && item.bbox.length === 4 ? [...item.bbox.slice(0, 4)] : null,
	all: item.all === true,
	boundaryShapeId: item.boundaryShapeId || undefined,
	boundaryShapeGroup: item.boundaryShapeGroup || undefined,
	parentStateShapeId: item.parentStateShapeId != null ? String(item.parentStateShapeId) : null,
	parentCountryShapeId:
		item.parentCountryShapeId != null ? String(item.parentCountryShapeId) : null,
});

/** Legacy bucketed read path — kept for facilities not yet re-saved under the flat-array payload. */
const structuredServiceAreasBucketsToAreas = (serviceAreasBuckets: any): any[] => {
	if (!serviceAreasBuckets || !isObject(serviceAreasBuckets) || isArray(serviceAreasBuckets)) {
		return [];
	}
	return flatMap(BUCKET_NAMES, (bucketName) => {
		const bucketItems = serviceAreasBuckets[bucketName];
		if (!isArray(bucketItems)) return [];
		const defaultPlaceType =
			bucketName === 'countries' ? 'country' : bucketName === 'states' ? 'region' : 'district';
		return filter(bucketItems, (item) => item && isObject(item) && !isArray(item)).map((item) =>
			serviceAreaItemToDisplayShape(item, defaultPlaceType)
		);
	});
};

const flatServiceAreasToAreas = (serviceAreasArray: any): any[] => {
	if (!isArray(serviceAreasArray)) {
		return [];
	}
	return filter(serviceAreasArray, (item) => item && isObject(item) && !isArray(item)).map((item) =>
		serviceAreaItemToDisplayShape(item, 'region')
	);
};

const getSupplierFacilityServiceAreasForDisplay = (
	facility: any
): { mapAreas: any[]; tagLabels: string[] } => {
	const rawServiceAreas = facility.serviceAreas;
	const areasFromServiceAreas = isArray(rawServiceAreas)
		? flatServiceAreasToAreas(rawServiceAreas)
		: structuredServiceAreasBucketsToAreas(rawServiceAreas);
	return {
		mapAreas: areasFromServiceAreas,
		tagLabels: areasFromServiceAreas.map((area) => area.name),
	};
};

interface Props {
	supplierFacility: any;
}

const ServiceAreasCard = ({ supplierFacility }: Props) => {
	const dispatch = useDispatch();
	const [editingServiceAreas, setEditingServiceAreas] = useState(false);
	const [resolvedAreas, setResolvedAreas] = useState<any[] | null>(null);
	const [apiAreas, setApiAreas] = useState<any[] | null>(null);

	const loadAreas = useCallback(() => {
		if (!supplierFacility || !supplierFacility.id) return;
		(dispatch as any)(fetchSupplierServiceAreas(supplierFacility.id))
			.then((response: any) => {
				const json = (response && response.json) || {};
				const items: any[] = Array.isArray(json) ? json : Array.isArray(json.data) ? json.data : [];
				const areas = items.map(serviceAreaFromApiItem).filter(Boolean) as any[];
				setApiAreas(areas);
				setResolvedAreas(null);
			})
			.catch(() => {
				setApiAreas(null);
			});
	}, [dispatch, supplierFacility]);

	useMountEffect(() => {
		loadAreas();
	});

	const rawAreas = useMemo(() => {
		if (apiAreas !== null) return apiAreas;
		return getSupplierFacilityServiceAreasForDisplay(supplierFacility || {}).mapAreas;
	}, [apiAreas, supplierFacility]);

	// Resolve CDN boundary geometries for the preview, then consolidate N-of-N children → parent.
	useEffect(() => {
		let cancelled = false;
		const needsResolve = rawAreas.some(function (a) {
			return !a.boundaries && a.boundaryShapeId && a.boundaryShapeGroup;
		});
		const continueWithConsolidation = (areasWithBoundaries: any[]) => {
			consolidateServiceAreasFromCdn(
				areasWithBoundaries,
				getStateFromArea,
				getCountryFromArea
			).then((consolidated) => {
				if (cancelled) return;
				setResolvedAreas(consolidated);
			});
		};
		if (!needsResolve) {
			continueWithConsolidation(rawAreas);
			return () => {
				cancelled = true;
			};
		}
		resolveServiceAreaBoundariesFromCDN(rawAreas.slice()).then((resolved) => {
			if (cancelled) return;
			continueWithConsolidation(resolved);
		});
		return () => {
			cancelled = true;
		};
	}, [rawAreas]);

	const mapAreas = resolvedAreas || rawAreas;
	const initialCenterOn = useMemo(
		() =>
			mapAreas.length === 0
				? USA_DEFAULT_CENTER_ON
				: getCenterOnTargetForAreas(mapAreas) || USA_DEFAULT_CENTER_ON,
		[mapAreas]
	);
	const visibleAreas = mapAreas.slice(0, SERVICE_AREAS_CARD_MAX_VISIBLE_TAGS);
	const extraTagCount =
		mapAreas.length > SERVICE_AREAS_CARD_MAX_VISIBLE_TAGS
			? mapAreas.length - SERVICE_AREAS_CARD_MAX_VISIBLE_TAGS
			: 0;

	return (
		<Row className="rowSpacing" gutter={16}>
			<Col span={24}>
				<Card>
					<h5 style={{ display: 'flex', alignItems: 'center' }}>
						Service Areas{' '}
						<Button className="ml-2" size="small" onClick={() => setEditingServiceAreas(true)}>
							Edit
						</Button>
					</h5>
					<Modal
						visible={editingServiceAreas}
						width="90vw"
						style={{ top: '5vh', paddingBottom: 0, maxWidth: 'none' }}
						bodyStyle={{
							height: '90vh',
							padding: '24px',
							overflow: 'hidden',
						}}
						className="[&_.ant-modal-body]:max-h-[unset] [&_.ant-modal-content]:h-[90vh]"
						onCancel={() => setEditingServiceAreas(false)}
						footer={null}
						closable={true}
						destroyOnClose={true}
					>
						<EditSupplierServiceAreasForm
							successCallback={() => {
								setEditingServiceAreas(false);
								loadAreas();
							}}
							cancelCallback={() => setEditingServiceAreas(false)}
							supplierFacility={supplierFacility}
						/>
					</Modal>
					<div className="relative w-full">
						{/* Wrapper must be positioned: Ant Design .ant-btn sets position:relative and overrides absolute on the button */}
						<div className="absolute right-2 top-2 z-20">
							<Button
								type="default"
								size="small"
								shape="circle"
								className="flex h-8 w-8 items-center justify-center border border-solid border-grey-5 bg-white p-0 !text-black/85 shadow-sm hover:!border-grey-6 hover:!text-black/85 focus:!border-grey-6 focus:!text-black/85 active:!border-grey-6 active:!text-black/85 hover:[&_.anticon]:!text-black/85 focus:[&_.anticon]:!text-black/85 active:[&_.anticon]:!text-black/85"
								icon={<FullscreenOutlined style={{ fontSize: 14, color: 'rgba(0,0,0,0.85)' }} />}
								title="Open map editor"
								aria-label="Open service areas map editor"
								onClick={() => setEditingServiceAreas(true)}
							/>
						</div>
						<ServiceAreaMap
							serviceAreas={mapAreas}
							height={350}
							progressiveGeoBoundaries={true}
							showSelectionMarkers={false}
							disableScrollZoom={true}
							// `'country'` disables the map's implied-highlight passes so the preview renders each row at its saved granularity.
							boundarySelectionMode="country"
							centerOn={initialCenterOn}
						/>
					</div>
					<div className="mt-3 flex flex-wrap gap-1">
						{mapAreas.length === 0 ? (
							<span className="bodyregular text-character-secondary">
								No service areas selected
							</span>
						) : (
							<>
								{visibleAreas.map((area, idx) => (
									<Tag key={idx} className="!mr-0 w-fit">
										{area.name || ''}
									</Tag>
								))}
								{extraTagCount > 0 ? (
									<Tag
										onClick={() => setEditingServiceAreas(true)}
										className="!mr-0 w-fit cursor-pointer"
									>
										+{extraTagCount} more
									</Tag>
								) : null}
							</>
						)}
					</div>
				</Card>
			</Col>
		</Row>
	);
};

export default ServiceAreasCard;
